import { Box } from '@mantine/core';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISegment, Segment } from '@feathr/blackbox';
import { SegmentStatHelper } from '@feathr/blackbox';
import { AnimatedNumber, DetailedProgressBar, Well } from '@feathr/components';

interface IProps {
  segment: Segment;
  mode: 'count' | 'crumbs' | 'reachable' | 'emails';
}

function SegmentGauge({ segment, mode }: Readonly<IProps>): JSX.Element {
  const statHelper = {
    lookback_mode: 'unbounded',
    lookback_value: 0,
    name: 'full segment',
    mode: 'match_any',
    predicates: [],
    account: '',
    is_custom: false,
  } as Partial<ISegment>;

  if (segment.get('parent_segment')) {
    statHelper['parent_segment'] = segment.get('parent_segment');
  }
  const [fullStatHelper] = useState(
    new SegmentStatHelper(observable<Partial<ISegment>>(statHelper)),
  );
  const { t } = useTranslation();

  const count = segment[mode] ?? 0;
  const fullCount = fullStatHelper[mode];

  useEffect(() => {
    if (count > fullCount) {
      // Force @compute to recompute, due to a new array being assigned to observable.
      runInAction(() => {
        fullStatHelper.segment.predicates = [];
      });
    }
  }, [count, fullCount, fullStatHelper.segment]);

  const descriptionMap = {
    start: {
      count: t('Person', { count }),
      reachable: t('Reachable person', { count }),
      crumbs: t('Breadcrumb', { count }),
      emails: t('Unique email address', { count }),
    },
    end: {
      count: t('People'),
      reachable: t('People'),
      crumbs: t('Breadcrumbs'),
      emails: t('Emails'),
    },
  };

  const isLoading = fullStatHelper.countPromise.state === 'pending';

  return (
    <Box mt={'2'}>
      <Well>
        <DetailedProgressBar
          endLabel={descriptionMap.end[mode]}
          endValue={fullCount}
          isLoading={isLoading}
          startValue={count}
          summary={
            <>
              <AnimatedNumber format={{ useGrouping: true }} value={count} /> /{' '}
              <AnimatedNumber format={{ useGrouping: true }} value={fullCount} />{' '}
              {t(descriptionMap.end[mode])}
            </>
          }
          value={count}
        />
      </Well>
    </Box>
  );
}

export default observer(SegmentGauge);
